import React, { useState } from 'react';
import { Button, Modal, Form, Input, DatePicker, Table, Space, Tag, Switch, Card, Typography, Row, Col } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, GithubOutlined, LinkOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '../../../contexts/ToastContext';
import apiService from '../../../services/api';
import { menteeProfileQueryKeys } from '../../../lib/queryClient';
import PanelTabCard from '../../../components/Common/PanelTabCard';
import TabListLoading from '../../../components/Common/TabListLoading';
import { MenteeProjectResponse, CreateMenteeProjectRequest, UpdateMenteeProjectRequest } from '../../../types';

const { Title } = Typography;

const ProjectTab: React.FC = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [searchText, setSearchText] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [editingProject, setEditingProject] = useState<MenteeProjectResponse | null>(null);
  const [form] = Form.useForm();

  const { data: projects = [], isLoading: loading } = useQuery({
    queryKey: menteeProfileQueryKeys.projects,
    queryFn: async () => {
      const res = await apiService.getMenteeProjects();
      if (!res.success) throw new Error(res.message || 'Projeler yüklenemedi.');
      return res.data;
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateMenteeProjectRequest) => apiService.createMenteeProject(data),
    onSuccess: (res) => {
      if (res.success) {
        toast.success('Proje başarıyla eklendi.');
        queryClient.invalidateQueries({ queryKey: menteeProfileQueryKeys.projects });
        setModalVisible(false);
        form.resetFields();
      } else toast.error(res.message || 'Proje eklenemedi.');
    },
    onError: () => toast.error('Projeler yüklenirken hata oluştu.'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateMenteeProjectRequest }) =>
      apiService.updateMenteeProject(id, data),
    onSuccess: (res) => {
      if (res.success) {
        toast.success('Proje başarıyla güncellendi.');
        queryClient.invalidateQueries({ queryKey: menteeProfileQueryKeys.projects });
        setModalVisible(false);
        setEditingProject(null);
        form.resetFields();
      } else toast.error(res.message || 'Proje güncellenemedi.');
    },
    onError: () => toast.error('Proje güncellenirken hata oluştu.'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiService.deleteMenteeProject(id),
    onSuccess: (res) => {
      if (res.success) {
        toast.success('Proje başarıyla silindi.');
        queryClient.invalidateQueries({ queryKey: menteeProfileQueryKeys.projects });
      } else toast.error(res.message || 'Proje silinemedi.');
    },
    onError: () => toast.error('Proje silinirken hata oluştu.'),
  });

  const handleAdd = () => {
    setEditingProject(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (project: MenteeProjectResponse) => {
    setEditingProject(project);
    form.setFieldsValue({
      projectName: project.projectName,
      projectDescription: project.projectDescription,
      githubUrl: project.githubUrl,
      liveUrl: project.liveUrl,
      technologies: project.technologies,
      startDate: project.startDate ? dayjs(project.startDate) : null,
      endDate: project.endDate ? dayjs(project.endDate) : null,
      isCompleted: project.isCompleted
    });
    setModalVisible(true);
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      const projectData = {
        projectName: values.projectName,
        projectDescription: values.projectDescription,
        githubUrl: values.githubUrl,
        liveUrl: values.liveUrl,
        technologies: values.technologies,
        startDate: values.startDate ? values.startDate.format('YYYY-MM-DD') : null,
        endDate: values.endDate ? values.endDate.format('YYYY-MM-DD') : null,
        isCompleted: values.isCompleted
      };

      if (editingProject) {
        updateMutation.mutate({ id: editingProject.id, data: projectData });
      } else {
        createMutation.mutate(projectData);
      }
    } catch (error) {
      // form validation
    }
  };

  const handleModalCancel = () => {
    setModalVisible(false);
    setEditingProject(null);
    form.resetFields();
  };

  const handleDelete = (id: number) => deleteMutation.mutate(id);

  // Filtreleme
  const filteredProjects = projects.filter(project =>
    project.projectName.toLowerCase().includes(searchText.toLowerCase()) ||
    (project.technologies && project.technologies.toLowerCase().includes(searchText.toLowerCase())) ||
    (project.projectDescription && project.projectDescription.toLowerCase().includes(searchText.toLowerCase()))
  );

  const columns = [
    {
      title: 'Proje Adı',
      dataIndex: 'projectName',
      key: 'projectName',
      sorter: (a: MenteeProjectResponse, b: MenteeProjectResponse) => a.projectName.localeCompare(b.projectName),
    },
    {
      title: 'Teknolojiler',
      dataIndex: 'technologies',
      key: 'technologies',
      render: (technologies: string) => {
        if (!technologies) return '-';
        return technologies.split(',').map((tech, index) => (
          <Tag key={index} color="blue" style={{ margin: '2px' }}>
            {tech.trim()}
          </Tag>
        ));
      },
    },
    {
      title: 'Başlangıç',
      dataIndex: 'startDate',
      key: 'startDate',
      render: (date: string) => date ? dayjs(date).format('MM/YYYY') : '-',
      sorter: (a: MenteeProjectResponse, b: MenteeProjectResponse) => 
        new Date(a.startDate || '').getTime() - new Date(b.startDate || '').getTime(),
    },
    {
      title: 'Bitiş',
      dataIndex: 'endDate',
      key: 'endDate',
      render: (date: string) => date ? dayjs(date).format('MM/YYYY') : '-',
    },
    {
      title: 'Durum',
      dataIndex: 'isCompleted',
      key: 'isCompleted',
      render: (isCompleted: boolean) => (
        <Tag color={isCompleted ? 'green' : 'orange'}>
          {isCompleted ? 'Tamamlandı' : 'Devam Ediyor'}
        </Tag>
      ),
      filters: [
        { text: 'Tamamlandı', value: true },
        { text: 'Devam Ediyor', value: false }
      ],
      onFilter: (value: boolean | React.Key, record: MenteeProjectResponse) => record.isCompleted === value,
    },
    {
      title: 'Linkler',
      key: 'links',
      render: (_: any, record: MenteeProjectResponse) => (
        <Space>
          {record.githubUrl && (
            <Button
              type="text"
              icon={<GithubOutlined />}
              href={record.githubUrl}
              target="_blank"
              style={{ color: 'var(--text-secondary)' }}
            />
          )}
          {record.liveUrl && (
            <Button
              type="text"
              icon={<LinkOutlined />}
              href={record.liveUrl}
              target="_blank"
              style={{ color: 'var(--text-secondary)' }}
            />
          )}
        </Space>
      ),
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_: any, record: MenteeProjectResponse) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            style={{ color: 'var(--text-secondary)' }}
          >
            Düzenle
          </Button>
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
          >
            Sil
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <>
    <PanelTabCard title="Projelerim" description="Projelerinizi ekleyin ve güncelleyin.">
        <TabListLoading loading={loading}>
        <Row justify="space-between" align="middle" style={{ marginBottom: '16px' }}>
          <Col>
          </Col>
          <Col>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
              style={{
                backgroundColor: 'var(--text-secondary)',
                borderColor: 'var(--text-secondary)',
                fontWeight: 500
              }}
            >
              Yeni Proje
            </Button>
          </Col>
        </Row>
        
        <Row style={{ marginBottom: '16px' }}>
          <Col span={8}>
            <Input
              placeholder="Proje adı, teknoloji veya açıklama ara..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ borderRadius: '6px' }}
            />
          </Col>
        </Row>

        <Table
          scroll={{ x: 'max-content' }}
          columns={columns}
          dataSource={filteredProjects}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} proje`
          }}
        />
        </TabListLoading>
    </PanelTabCard>

      <Modal
        title={editingProject ? 'Proje Düzenle' : 'Yeni Proje'}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        width={700}
        okText={editingProject ? 'Güncelle' : 'Ekle'}
        cancelText="İptal"
        okButtonProps={{
          style: { backgroundColor: 'var(--text-secondary)', borderColor: 'var(--text-secondary)' }
        }}
      >
        <Form layout="vertical" form={form} requiredMark={false}>
          <Form.Item 
            name="projectName" 
            label="Proje Adı" 
            rules={[{ required: true, message: 'Proje adı gerekli' }]}
          >
            <Input placeholder="Örn: E-ticaret Web Sitesi" />
          </Form.Item>

          <Form.Item 
            name="projectDescription" 
            label="Proje Açıklaması"
          >
            <Input.TextArea 
              rows={4} 
              placeholder="Proje hakkında detaylı açıklama..."
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item 
                name="githubUrl" 
                label="GitHub URL"
              >
                <Input placeholder="https://github.com/username/repo" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item 
                name="liveUrl" 
                label="Canlı Proje URL"
              >
                <Input placeholder="https://example.com" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item 
            name="technologies" 
            label="Kullanılan Teknolojiler"
          >
            <Input placeholder="React, Node.js, MongoDB (virgülle ayırın)" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item 
                name="startDate" 
                label="Başlangıç Tarihi"
                rules={[
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      const today = dayjs().startOf('month');
                      const selectedDate = dayjs(value).startOf('month');
                      if (selectedDate.isAfter(today)) {
                        return Promise.reject(new Error('Başlangıç tarihi bugünden sonra olamaz'));
                      }
                      const endDate = form.getFieldValue('endDate');
                      if (endDate && selectedDate.isAfter(dayjs(endDate).startOf('month'))) {
                        return Promise.reject(new Error('Başlangıç tarihi bitiş tarihinden sonra olamaz'));
                      }
                      return Promise.resolve();
                    }
                  }
                ]}
              >
                <DatePicker 
                  style={{ width: '100%' }} 
                  picker="month"
                  placeholder="Başlangıç"
                  disabledDate={(current) => current && current.isAfter(dayjs(), 'month')}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item 
                name="endDate" 
                label="Bitiş Tarihi"
                dependencies={['startDate']}
                rules={[
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      const startDate = form.getFieldValue('startDate');
                      if (startDate && dayjs(value).startOf('month').isBefore(dayjs(startDate).startOf('month'))) {
                        return Promise.reject(new Error('Bitiş tarihi başlangıç tarihinden önce olamaz'));
                      }
                      return Promise.resolve();
                    }
                  }
                ]}
              >
                <DatePicker 
                  style={{ width: '100%' }} 
                  picker="month"
                  placeholder="Bitiş"
                  disabledDate={(current) => {
                    const startDate = form.getFieldValue('startDate');
                    if (startDate && current) {
                      return current.isBefore(dayjs(startDate), 'month');
                    }
                    return false;
                  }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item 
            name="isCompleted" 
            label="Tamamlandı" 
            valuePropName="checked" 
            initialValue={false}
            dependencies={['startDate']}
            rules={[
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  const startDate = form.getFieldValue('startDate');
                  if (startDate && dayjs(startDate).isAfter(dayjs(), 'month')) {
                    return Promise.reject(new Error('Gelecekteki bir tarih için tamamlandı durumu seçilemez'));
                  }
                  return Promise.resolve();
                }
              }
            ]}
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default ProjectTab;
